import { DataWithKey } from '../../Component/EnhancedData/Types'
import * as EDT from '../../Type/EnhancedData'
import { LineItem, LineItemStateData } from '../../Type/EnhancedData'
import { FieldMap } from '../../types'
import { toMap } from '../util'
import { getParsedBackendValidationError } from './BackendValidationErrorParser'
import { defaultMEDFormStateData, initialProductItem } from './Constants'
import {
  isEmptyLineItem,
  toConsumableNumber,
  toSource,
  toTargetDataWithKey,
  updateLineItemIndices,
} from './EnhancedDataUtils'

export const LINE_ITEM_FIELD_MAP: FieldMap<EDT.LineItem, EDT.LineItemStateData> = {
  product_label: 'productName',
  quantity: 'quantity',
  unit_of_measure: 'unitOfMeasure',
  unit_cost: 'costPerUnit',
  item_total_amount: 'amount',
  item_tax_amount: 'itemTax',
  product_code: 'productCode',
  commodity_code: 'commodityCode',
  discount_amount: 'itemDiscountAmount',
  product_id: 'productId',
  product_source: 'productSource',
  line_item_id: 'lineItemId',
  is_aggregated_record: 'isAggregatedRecord',
  was_aggregated: 'wasAggregated',
}

export const moneyConsumerFormatter = (amountFormatted?: string): number => {
  if (!amountFormatted) return 0
  const cleanString = amountFormatted.replace(/[$,]/g, '').trim()
  const parsedValue = parseFloat(cleanString)
  return isNaN(parsedValue) ? 0 : parsedValue
}

export const toMappedLineItems = (lineItems: EDT.LineItem[]): Record<string, DataWithKey<EDT.LineItemStateData>> => {
  const lineItemWithKey: DataWithKey<EDT.LineItemStateData>[] = lineItems.map(
    (source, index) => (
      {
        ...toTargetDataWithKey(source, LINE_ITEM_FIELD_MAP, initialProductItem),
        index,
        rowNumber: (index + 1),
      }
    )
  )
  return toMap(lineItemWithKey)
}

export const toMEDFormData = (transaction?: EDT.TransactionSearchRecordForManualFill): EDT.MEDFormStateData => {
  return {
    customerReferenceId: transaction?.customer_reference_id || defaultMEDFormStateData.customerReferenceId,
    customerTaxId: transaction?.masked_customer_tax_id || defaultMEDFormStateData.customerTaxId,
    sourceZipCode: transaction?.source_address?.postal_code || defaultMEDFormStateData.shippingZipCode,
    shippingCountry: transaction?.shipping_address?.country || defaultMEDFormStateData.shippingCountry,
    shippingZipCode: transaction?.shipping_address?.postal_code || defaultMEDFormStateData.shippingZipCode,
    invoice: transaction?.invoice_number || defaultMEDFormStateData.invoice,
    merchantTaxId: transaction?.masked_merchant_tax_id || defaultMEDFormStateData.merchantTaxId,
    taxAmount: toConsumableNumber(transaction?.tax_amount, defaultMEDFormStateData.taxAmount),
    freightAmount: toConsumableNumber(transaction?.freight_amount, defaultMEDFormStateData.freightAmount),
    discountAmount: toConsumableNumber(transaction?.discount_amount, defaultMEDFormStateData.discountAmount),
    surchargeAmount: toConsumableNumber(transaction?.surcharge_amount, defaultMEDFormStateData.surchargeAmount),
    surchargeTaxAmount: toConsumableNumber(transaction?.surcharge_tax_amount, defaultMEDFormStateData.surchargeTaxAmount),
    lineItemMap: transaction?.line_items ? toMappedLineItems(transaction.line_items) : {},
  }
}

export const getUpdatedLineItemMap = <K extends keyof EDT.LineItemStateData>(
  lineItemMap: Record<string, DataWithKey<EDT.LineItemStateData>>,
  key: string | number,
  fieldName: K,
  updatedValue: EDT.LineItemStateData[K]
): Record<string, DataWithKey<EDT.LineItemStateData>> => {
  const stringKey = String(key)
  if (!lineItemMap[stringKey]) return lineItemMap

  return {
    ...lineItemMap,
    [stringKey]: { ...lineItemMap[stringKey], [fieldName]: updatedValue },
  }
}

export const removeLineItemFromMap = (
  lineItemMap: Record<string, DataWithKey<EDT.LineItemStateData>>,
  keyToRemove: string | number,
): Record<string, DataWithKey<EDT.LineItemStateData>> => {
  const { [String(keyToRemove)]: removedItem, ...rest } = lineItemMap
  const isLastRecord = removedItem.index === Object.keys(lineItemMap).length - 1
  return isLastRecord ? rest : updateLineItemIndices(rest)
}

export const addLineItemToMap = (
  data: DataWithKey<EDT.LineItemStateData>,
  lineItemMap?: Record<string, DataWithKey<EDT.LineItemStateData>>,
): Record<string, DataWithKey<EDT.LineItemStateData>> => {
  return {
    ...lineItemMap,
    [data.key]: data,
  }
}

export const clearLineItemTaxAmountsFromMap = (
  lineItemMap: Record<string, DataWithKey<LineItemStateData>>,
): Record<string, DataWithKey<LineItemStateData>> => {
  const updatedEntries = Object.entries(lineItemMap).map(([key, lineItemData]) => {
    const updatedLineItem: DataWithKey<LineItemStateData> = {
      ...lineItemData,
      itemTax: 0,
    }
    return [key, updatedLineItem]
  })
  return Object.fromEntries(updatedEntries)
}

const toLineItemPayload = (lineItemMap?: Record<string, DataWithKey<LineItemStateData>>): LineItem[] => {
  // A pipeline to extract only valid line items from the record
  return Object.values(lineItemMap || {})
    .map(({ key, ...itemWithoutKey }) => itemWithoutKey) // extracting key from line item data
    .map((itemWithoutKey) => {
      return {
        ...itemWithoutKey
      }
    })
    .filter((item) => !isEmptyLineItem(item)) // omitting untouched items
    .map((cleanItem) => toSource<LineItem, LineItemStateData>(cleanItem, LINE_ITEM_FIELD_MAP)) // convert to actual line item payload
}

export const toEnhancedDataRequest = (
  merchantId: number,
  transactionRecord: EDT.TransactionSearchRecordForManualFill | undefined,
  medFormData: EDT.MEDFormStateData
): EDT.EnhancedDataRequest => {
  return {
    merchant_id: merchantId,
    customer_reference_id: medFormData.customerReferenceId || '',
    merchant_tax_id: medFormData.merchantTaxId || '',
    customer_tax_id: medFormData.customerTaxId || '',
    tax_amount: medFormData.taxAmount || 0,
    invoice_number: medFormData.invoice || '',
    national_tax_amount: 0,
    freight_amount: medFormData.freightAmount || 0,
    discount_amount: medFormData.discountAmount || 0,
    additional_tax_amount: 0,
    additional_tax_included: false,
    additional_tax_rate: 0,
    surcharge_tax_amount: medFormData?.surchargeTaxAmount ?? transactionRecord?.surcharge_tax_amount ?? 0,
    shipping_address: {
      country: medFormData.shippingCountry || transactionRecord?.shipping_address?.country || '',
      postal_code: medFormData.shippingZipCode || transactionRecord?.shipping_address?.postal_code || '',
    } as EDT.ShippingAddress,
    source_address: {
      postal_code: medFormData.sourceZipCode || transactionRecord?.source_address?.postal_code || '',
    } as EDT.SourceAddress,
    line_items: toLineItemPayload(medFormData.lineItemMap),
  }
}

export const toEnhancedDataTransactionsRequest = (
  merchantId: number,
  offset: number,
  limit: number,
  is_enhanced_data_required: boolean
): EDT.EnhancedDataTransactionsRequest => {
  return {
    merchant_id: merchantId,
    offset,
    limit,
    is_enhanced_data_required,
  }
}

export const toMappedBackendValidationError = (
  err: EDT.EnhancedDataRawError | undefined | null
): EDT.EnhancedDataBackendError | null => {
  if (!err) return null
  if (!err?.errors) return null
  if (Object.keys(err?.errors).length === 0) return null
  return getParsedBackendValidationError(err.errors, LINE_ITEM_FIELD_MAP)
}
